import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Airplane, BuildingApartment, MapTrifold, Boat, CurrencyDollar, MapPin, CalendarBlank,
  CircleNotch, Envelope, SealCheck, Clock, Car, ShieldCheck, Phone, CheckCircle, Star,
  CreditCard, ArrowSquareOut, Sparkle, DownloadSimple, ChatCircleText, ForkKnife,
  Sun, Note, Warning, X, ClipboardText, FileText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { parseDate, formatDate, formatTime, tripDayCount, tripNightCount, dayOffsetLabel } from "@/lib/dateUtils";
import { buildDayTimeline } from "@/lib/buildDayTimeline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SectionLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      <div className="flex items-center gap-2 text-[#D4AF37] text-[11px] uppercase tracking-[0.35em] font-sans">
        <Icon size={16} weight="duotone" />
        {label}
      </div>
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110"
          data-testid={`star-${n}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star size={32} weight={n <= value ? "fill" : "regular"} className="text-[#D4AF37]" />
        </button>
      ))}
    </div>
  );
}

const DAY_ITEM_ICONS = {
  flight: Airplane,
  hotel: BuildingApartment,
  tour: MapTrifold,
  activity: Sparkle,
  transportation: Car,
  cruise: Boat,
  restaurant: ForkKnife,
  free: Sun,
  note: Note,
};

function DayItemRow({ item, dayNumber, idx }) {
  const [open, setOpen] = useState(false);
  const Icon = DAY_ITEM_ICONS[item.type] || Note;
  const isFlight = item.type === "flight";
  const isHotel = item.type === "hotel";
  // Type-specific extras gate the "Details" expander too
  const hasFlightDetails = isFlight && (item.airline || item.flight_number || item.from_airport || item.to_airport || item.departure_date || item.arrival_date || item.cabin_class);
  const hasHotelDetails = isHotel && (item.hotel_name || item.address || item.check_in_date || item.check_out_date || item.room_type);
  const hasExtras = item.confirmation_number || item.supplier_contact || item.price || item.link || item.description || hasFlightDetails || hasHotelDetails;
  // Maps target: hotel address > generic location
  const mapsTarget = isHotel ? (item.address || item.location) : item.location;
  const mapsUrl = mapsTarget ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsTarget)}` : null;
  // Compute a smart title for type-specific items when agent left title blank
  const echoArrival = item._echo === "arrival";
  const echoCheckout = item._echo === "checkout";
  const computedTitle = echoArrival
    ? `Arriving · ${item.airline || ""} ${item.flight_number || ""}`.trim()
    : echoCheckout
    ? `Check-out · ${item.hotel_name || "Hotel"}`
    : (item.title
      || (isFlight && (item.airline || item.flight_number)
          ? `${item.airline || ""} ${item.flight_number || ""}`.trim()
          : null)
      || (isHotel && item.hotel_name ? `Check-in · ${item.hotel_name}` : null)
      || (item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : "Item"));
  // Subtitle line under the title for flight/hotel quick-glance
  let subtitle = null;
  if (isFlight) {
    const route = [item.from_airport, item.to_airport].filter(Boolean).join(" → ");
    subtitle = route || null;
  } else if (isHotel) {
    subtitle = item.address || item.location || null;
  } else {
    subtitle = item.location || null;
  }
  return (
    <li className="relative" data-testid={`client-day-${dayNumber}-item-${idx}`}>
      <div className="absolute -left-6 sm:-left-8 top-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-ink border border-[#D4AF37]/60 flex items-center justify-center">
        <Icon size={12} weight="duotone" className="text-[#D4AF37]" />
      </div>
      <div className="pl-1">
        <button
          type="button"
          onClick={() => hasExtras && setOpen((v) => !v)}
          disabled={!hasExtras}
          className={`text-left w-full ${hasExtras ? "cursor-pointer group" : "cursor-default"}`}
          data-testid={`day-item-toggle-${dayNumber}-${idx}`}
          aria-expanded={open}
        >
          <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
            {item.time && (
              <span className="text-[11px] font-bold text-[#D4AF37] tracking-wider tabular-nums">{item.time}</span>
            )}
            <h4 className="font-serif text-lg text-white leading-tight group-hover:text-[#F3C853] transition-colors">
              {computedTitle}
            </h4>
            {hasExtras && (
              <span className="text-[10px] text-white/30 group-hover:text-[#D4AF37] transition-colors uppercase tracking-widest font-sans">
                {open ? "Hide details" : "Details"}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-white/60 mb-1 flex items-center gap-1.5 font-sans">
              {isFlight ? <Airplane size={11} weight="duotone" /> : <MapPin size={11} weight="duotone" />}
              {subtitle}
            </p>
          )}
        </button>

        {open && hasExtras && (
          <div className="mt-2 pl-3 border-l-2 border-[#D4AF37]/20 py-1 space-y-2 animate-fade-up" data-testid={`day-item-details-${dayNumber}-${idx}`}>
            {/* Flight structured details */}
            {isFlight && hasFlightDetails && (
              <div className="rounded-md bg-black/30 border border-[#D4AF37]/15 px-3 py-2.5 space-y-1.5">
                {(item.airline || item.flight_number || item.cabin_class) && (
                  <p className="text-[12px] text-white/85 font-sans">
                    <span className="text-white">{item.airline || ""}</span>
                    {item.flight_number && <span className="text-[#D4AF37] ml-1.5">{item.flight_number}</span>}
                    {item.cabin_class && <span className="text-white/50 ml-2">· {item.cabin_class}</span>}
                  </p>
                )}
                {(item.from_airport || item.to_airport) && (
                  <p className="text-[12px] text-white/70 font-sans">
                    {item.from_airport || "—"} → {item.to_airport || "—"}
                  </p>
                )}
                {(item.departure_date || item.departure_time) && (
                  <p className="text-[11px] text-white/60 font-sans">
                    <span className="text-white/40">Departs · </span>
                    {formatDate(item.departure_date)} {item.departure_time && <span className="text-[#D4AF37] ml-1">{formatTime(item.departure_time)}</span>}
                  </p>
                )}
                {(item.arrival_date || item.arrival_time) && (
                  <p className="text-[11px] text-white/60 font-sans">
                    <span className="text-white/40">Arrives · </span>
                    {formatDate(item.arrival_date)} {item.arrival_time && <span className="text-[#D4AF37] ml-1">{formatTime(item.arrival_time)}</span>}
                    {dayOffsetLabel(item.departure_date, item.arrival_date) && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[9px] font-sans uppercase tracking-wider" data-testid="flight-day-offset">
                        {dayOffsetLabel(item.departure_date, item.arrival_date)}
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
            {/* Hotel structured details */}
            {isHotel && hasHotelDetails && (
              <div className="rounded-md bg-black/30 border border-[#D4AF37]/15 px-3 py-2.5 space-y-1.5">
                {item.hotel_name && (
                  <p className="text-[12px] text-white font-serif">{item.hotel_name}</p>
                )}
                {(item.check_in_date || item.check_out_date) && (
                  <p className="text-[11px] text-white/65 font-sans">
                    <span className="text-white/40">Check-in · </span>
                    {formatDate(item.check_in_date)} {item.check_in_time && <span className="text-[#D4AF37] ml-1">{formatTime(item.check_in_time)}</span>}
                    <span className="mx-2 text-white/25">→</span>
                    <span className="text-white/40">Out · </span>
                    {formatDate(item.check_out_date)} {item.check_out_time && <span className="text-[#D4AF37] ml-1">{formatTime(item.check_out_time)}</span>}
                  </p>
                )}
                {item.room_type && (
                  <p className="text-[11px] text-white/65 font-sans">
                    <span className="text-white/40">Room · </span>{item.room_type}
                  </p>
                )}
                {item.address && (
                  <p className="text-[11px] text-white/65 font-sans">
                    <span className="text-white/40">Address · </span>{item.address}
                  </p>
                )}
              </div>
            )}
            {item.description && (
              <p className="text-sm text-white/75 leading-relaxed font-sans">{item.description}</p>
            )}
            {(item.confirmation_number || item.supplier_contact || item.price) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-white/55 font-sans">
                {item.confirmation_number && (
                  <span><span className="text-white/35">Conf · </span><span className="text-white/85 font-mono">{item.confirmation_number}</span></span>
                )}
                {item.supplier_contact && (
                  <span><span className="text-white/35">Contact · </span>{item.supplier_contact}</span>
                )}
                {item.price && <span className="text-[#D4AF37]">{item.currency || ""} {item.price}</span>}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-colors text-[11px] font-sans"
                  data-testid={`item-doc-link-${dayNumber}-${idx}`}
                >
                  <FileText size={12} weight="duotone" /> View document
                </a>
              )}
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37]/80 hover:text-[#D4AF37] hover:underline font-sans" data-testid={`item-map-link-${dayNumber}-${idx}`}>
                  <MapPin size={11} weight="duotone" /> View on map
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

function dayPill(items) {
  /* Simplified per agent feedback — only "Travel Day" pill (when flights /
   * transportation / cruises / railways are present). All other days run
   * without a pill so the layout stays clean and date-driven.
   */
  if (!Array.isArray(items) || items.length === 0) return null;
  const types = items.map((x) => x.type);
  if (types.includes("flight") || types.includes("transportation") || types.includes("cruise") || types.includes("railway")) {
    return { label: "Travel Day", tone: "travel" };
  }
  return null;
}

const PILL_TONES = {
  travel: "bg-blue-500/10 text-blue-200 border-blue-400/40",
};

const PILL_ICONS = {
  travel: Airplane,
};

function DayCard({ day }) {
  const items = day.items || [];
  const pill = dayPill(items);
  const PillIcon = pill ? PILL_ICONS[pill.tone] : null;
  return (
    <article className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl overflow-hidden" data-testid={`client-day-${day.day_number}`}>
      {day.cover_image_url && (
        <div className="relative h-48 sm:h-56 overflow-hidden">
          <img src={day.cover_image_url} alt={day.title || `Day ${day.day_number}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-ink-surface/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Day {day.day_number}</p>
              {pill && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-sans ${PILL_TONES[pill.tone]}`}
                  data-testid={`day-${day.day_number}-pill`}
                >
                  {PillIcon && <PillIcon size={10} weight="duotone" />} {pill.label}
                </span>
              )}
            </div>
            {day.date && <h3 className="font-serif text-3xl text-white leading-tight">{formatDate(day.date)}</h3>}
            {day.title && <p className="text-sm text-white/70 font-sans mt-1">{day.title}</p>}
          </div>
        </div>
      )}
      <div className="p-6 sm:p-7">
        {!day.cover_image_url && (
          <header className="mb-4 pb-4 border-b border-white/10">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans" data-testid={`day-${day.day_number}-label`}>
                Day {day.day_number}
              </p>
              {pill && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-sans ${PILL_TONES[pill.tone]}`}
                  data-testid={`day-${day.day_number}-pill`}
                >
                  {PillIcon && <PillIcon size={10} weight="duotone" />} {pill.label}
                </span>
              )}
            </div>
            {day.date && (
              <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight" data-testid={`day-${day.day_number}-date`}>
                {formatDate(day.date)}
              </h3>
            )}
            {day.title && (
              <p className="text-sm text-white/70 font-sans mt-1">{day.title}</p>
            )}
          </header>
        )}

        {day.summary && (
          <p className="font-serif italic text-white/80 mb-5">{day.summary}</p>
        )}

        <ol className="relative pl-6 sm:pl-8 space-y-5 before:content-[''] before:absolute before:left-3 sm:before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-[#D4AF37]/25">
          {items.length === 0 ? (
            <li className="text-white/40 text-sm italic font-sans">Day to be confirmed.</li>
          ) : items.map((it, i) => (
            <DayItemRow key={it.id || i} item={it} dayNumber={day.day_number} idx={i} />
          ))}
        </ol>
      </div>
    </article>
  );
}

// Status pill colors for client-facing payment status (translated from internal).
const CLIENT_STATUS_PILL = {
  "Not Started": "bg-white/5 text-white/50 border-white/10",
  "Sent": "bg-blue-500/10 text-blue-300 border-blue-400/30",
  "Pending": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Paid": "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
};

const PAY_LABEL_PILL = {
  "Deposit Required": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Full Payment": "bg-blue-500/10 text-blue-300 border-blue-400/30",
  "Optional": "bg-white/5 text-white/50 border-white/10",
  "Pay Later": "bg-purple-500/10 text-purple-300 border-purple-400/30",
};

function PayChecklistItem({ sl, index, onAuthRequest }) {
  const status = sl.client_status || "Not Started";
  const isPaid = status === "Paid";
  const isPending = status === "Pending";
  const hasAuth = !!sl.has_authorization;

  // Decide action based on payment_mode override + payment_link presence.
  // mode "direct"        → Pay Now if link present, else fall through to auth (with hint)
  // mode "authorization" → Submit Payment Details (always, even if link is set)
  // mode ""              → automatic: link present → Pay Now; else → Submit Payment Details
  const mode = sl.payment_mode || "";
  const hasLink = !!(sl.payment_link || "").trim();
  // wantsAuth covers: explicit auth, auto + no link, OR direct + no link (graceful fallback so a button always exists)
  const wantsAuth = mode === "authorization" || (!hasLink && (mode === "" || mode === "direct"));
  const showPayNow = !wantsAuth && hasLink;
  const showSubmitAuth = wantsAuth;
  const linkMissingForDirectMode = mode === "direct" && !hasLink;

  const handlePay = () => {
    if (!hasLink) return;
    window.open(sl.payment_link, "_blank", "noopener,noreferrer");
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sl.payment_link || "");
      toast.success("Payment link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <li
      className={`group rounded-2xl border transition-all p-5 sm:p-6 ${
        isPaid
          ? "border-emerald-400/40 bg-emerald-500/5"
          : "border-[#D4AF37]/25 bg-ink-surface hover:border-[#D4AF37]/50"
      }`}
      data-testid={`pay-supplier-${(sl.category || "other").toLowerCase()}-${sl.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 sm:w-auto">
          <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center border ${
            isPaid ? "border-emerald-400/50 bg-emerald-500/10" : "border-[#D4AF37]/30 bg-[#D4AF37]/5"
          }`}>
            {isPaid ? (
              <CheckCircle size={18} weight="fill" className="text-emerald-300" />
            ) : (
              <span className="font-serif text-base text-[#D4AF37]">{index + 1}</span>
            )}
          </div>
          <div className="sm:hidden flex-1">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">{sl.category || "Supplier"}</p>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="hidden sm:block text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-0.5 font-sans">{sl.category || "Supplier"}</p>
          <p className="font-serif text-xl text-white leading-tight">Pay {sl.category || "Supplier"}</p>
          {sl.supplier_name && <p className="text-sm text-white/60 mt-0.5">{sl.supplier_name}</p>}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {sl.payment_label && (
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${
                  PAY_LABEL_PILL[sl.payment_label] || "bg-white/5 text-white/60 border-white/10"
                }`}
                data-testid={`label-pill-${sl.id}`}
              >
                {sl.payment_label}
              </span>
            )}
            {hasAuth && !isPaid && (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-300 border-emerald-400/30" data-testid={`auth-submitted-pill-${sl.id}`}>
                <CheckCircle size={11} weight="fill" /> Authorization submitted
              </span>
            )}
            {(status !== "Not Started") && !hasAuth && (
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans inline-flex items-center gap-1 ${CLIENT_STATUS_PILL[status]}`}
                data-testid={`status-pill-${sl.id}`}
              >
                {isPaid ? <CheckCircle size={11} weight="fill" /> : isPending ? <Clock size={11} weight="duotone" /> : null}
                {status}
              </span>
            )}
          </div>
          {sl.client_note && (
            <p className="text-xs text-white/55 mt-2 italic">{sl.client_note}</p>
          )}
          {linkMissingForDirectMode && (
            <p className="text-xs text-amber-300/80 mt-2 italic">No payment link on file — please contact your travel designer.</p>
          )}
        </div>

        <div className="flex items-center gap-2 sm:flex-col sm:items-stretch lg:flex-row lg:items-center">
          {!isPaid && showPayNow && (
            <Button
              onClick={handlePay}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 px-5 text-xs uppercase tracking-[0.25em]"
              data-testid={`pay-now-${sl.id}`}
            >
              <ArrowSquareOut size={14} weight="duotone" /> Pay Now
            </Button>
          )}
          {!isPaid && showSubmitAuth && (
            <Button
              onClick={() => onAuthRequest(sl)}
              disabled={hasAuth}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 px-5 text-xs uppercase tracking-[0.25em] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`submit-auth-${sl.id}`}
            >
              <CreditCard size={14} weight="duotone" /> {hasAuth ? "Submitted" : "Submit Payment Details"}
            </Button>
          )}
          {!isPaid && showPayNow && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCopy}
              className="border-white/15 bg-transparent text-white/70 hover:bg-white/5 hover:text-white h-11 px-3 text-xs uppercase tracking-[0.25em]"
              aria-label="Copy payment link"
              data-testid={`copy-link-${sl.id}`}
              title="Copy payment link"
            >
              <ClipboardText size={14} weight="duotone" /> Copy
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}


export default function ClientView() {
  const { token } = useParams();
  const [itin, setItin] = useState(null);
  const [error, setError] = useState(null);

  // action states
  const [approving, setApproving] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  // payment form
  const [paymentType, setPaymentType] = useState("full");
  const [payForm, setPayForm] = useState({ cardholder_name: "", card_last4: "", card_exp: "", billing_zip: "", signature: "", notes: "" });
  const [paySubmitting, setPaySubmitting] = useState(false);

  // per-supplier authorization dialog
  const [supplierAuthTarget, setSupplierAuthTarget] = useState(null);
  const [supplierAuthForm, setSupplierAuthForm] = useState({ cardholder_name: "", card_last4: "", card_exp: "", billing_zip: "", signature: "", notes: "" });
  const [supplierAuthSubmitting, setSupplierAuthSubmitting] = useState(false);

  // review form
  const [review, setReview] = useState({ rating: 5, testimonial: "", client_name: "", allow_public: false });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // PDF download
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const loadItinerary = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/itineraries/${token}`);
      setItin(data);
      setReview((p) => ({ ...p, client_name: p.client_name || data.client_name || "" }));
    } catch {
      setError("This itinerary is not available.");
    }
  };

  useEffect(() => { loadItinerary(); }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-serif text-3xl text-white mb-2">Link unavailable</h1>
          <p className="text-white/60 text-sm font-sans">{error}</p>
        </div>
      </div>
    );
  }

  if (!itin) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <CircleNotch size={28} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const status = itin.status || "Draft";
  const hasAny = (k) => (itin[k] || []).length > 0;
  const hasDays = Array.isArray(itin.days) && itin.days.length > 0;
  const canApprove = ["Draft", "Sent", "Declined"].includes(status);
  const canDecline = ["Draft", "Sent"].includes(status);
  const canDownloadPdf = !["Draft"].includes(status);
  const isDeclined = status === "Declined";
  const canPay = ["Approved", "Payment Link Sent", "Revised"].includes(status);
  const tripDays = tripDayCount(itin.start_date, itin.end_date);
  const tripNights = tripNightCount(itin.start_date, itin.end_date);
  const totalPrice = (itin.pricing || {}).total;
  const inclusions = itin.inclusions || [];
  const exclusions = itin.exclusions || [];
  const agentCard = itin.agent || {};
  const pdfUrl = `${BACKEND_URL}/api/public/itineraries/${token}/pdf`;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const downloadPdf = async () => {
    if (pdfDownloading) return;
    setPdfDownloading(true);
    const toastId = toast.loading("Generating your travel documents…");
    // Give the server up to 45s (worst-case fallback ladder = 8+10+12 + slack)
    // and surface an explicit timeout instead of hanging the button forever.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);
    try {
      const resp = await fetch(pdfUrl, { method: "GET", signal: controller.signal });
      if (!resp.ok) {
        let detail = "";
        try { detail = (await resp.json()).detail || ""; } catch { /* ignore */ }
        throw new Error(detail || `PDF request failed (HTTP ${resp.status})`);
      }
      // Surface which tier produced the file — useful for support triage.
      const tier = resp.headers.get("X-PDF-Mode") || "standard";
      const blob = await resp.blob();
      if (!blob || blob.size < 200) {
        throw new Error("The generated PDF was empty. Please try again in a moment.");
      }
      const url = URL.createObjectURL(blob);
      const safeTitle = (itin.title || "itinerary").replace(/[^a-z0-9 _-]/gi, "").trim() || "itinerary";
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeTitle} - Travel Documents.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // eslint-disable-next-line no-console
      console.info(`[PDF] Downloaded ${blob.size} bytes via tier=${tier}`);
      toast.success("Your travel documents are ready", { id: toastId });
    } catch (e) {
      let msg;
      if (e?.name === "AbortError") {
        msg = "The PDF is taking longer than usual to generate. Please try again in a minute — if it keeps failing, contact your travel advisor.";
      } else if (e?.message?.includes("Failed to fetch")) {
        msg = "Network issue while generating your PDF. Please check your connection and try again.";
      } else {
        msg = e?.message || "Could not generate PDF. Please try again shortly.";
      }
      toast.error(msg, { id: toastId });
    } finally {
      clearTimeout(timeoutId);
      setPdfDownloading(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — share it with anyone");
    } catch { toast.error("Could not copy link"); }
  };

  const downloadCalendar = () => {
    // Build .ics file from flights + day items (flight/tour/cruise/restaurant)
    const events = [];
    const fmt = (s) => {
      if (!s) return null;
      try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return null;
        return d.toISOString().replace(/[-:.]/g, "").replace("000Z", "Z");
      } catch { return null; }
    };
    const fmtAllDay = (s) => {
      if (!s) return null;
      try {
        return s.replace(/-/g, "");
      } catch { return null; }
    };
    (itin.flights || []).forEach((f) => {
      // Build datetime from new split fields, fall back to legacy combined `departure`/`arrival`.
      const buildDT = (date, time, legacy) => {
        if (date && time) return fmt(`${date}T${time}`);
        if (date) return fmt(date);
        return fmt(legacy);
      };
      const dep = buildDT(f.departure_date, f.departure_time, f.departure);
      if (!dep) return;
      const arr = buildDT(f.arrival_date, f.arrival_time, f.arrival) || dep;
      events.push({
        uid: `flt-${f.id}`,
        start: dep,
        end: arr,
        summary: `Flight ${f.flight_number || ""} · ${f.from_city || f.from_airport || ""} → ${f.to_city || f.to_airport || ""}`,
        location: `${f.from_airport || ""} to ${f.to_airport || ""}`,
        description: `${f.airline || ""} · ${f.cabin_class || ""}`.trim(),
      });
    });
    (itin.days || []).forEach((d) => {
      (d.items || []).forEach((it) => {
        if (!["flight", "tour", "activity", "cruise", "restaurant"].includes(it.type)) return;
        const baseDate = fmtAllDay(d.date);
        if (!baseDate) return;
        events.push({
          uid: `${d.id}-${it.id}`,
          dtstart: baseDate,
          summary: `${it.title || it.type}${it.time ? ` · ${it.time}` : ""}`,
          location: it.location || "",
          description: it.description || "",
          isAllDay: true,
        });
      });
    });
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Best Time Travel & Tours//Itinerary//EN", "CALSCALE:GREGORIAN"];
    events.forEach((e) => {
      lines.push("BEGIN:VEVENT");
      lines.push(`UID:${e.uid}@besttimetravelandtours.com`);
      lines.push(`SUMMARY:${(e.summary || "").replace(/[\n\r,;]/g, " ")}`);
      if (e.location) lines.push(`LOCATION:${e.location.replace(/[\n\r,;]/g, " ")}`);
      if (e.description) lines.push(`DESCRIPTION:${e.description.replace(/[\n\r,;]/g, " ")}`);
      if (e.isAllDay) {
        lines.push(`DTSTART;VALUE=DATE:${e.dtstart}`);
      } else {
        lines.push(`DTSTART:${e.start}`);
        if (e.end) lines.push(`DTEND:${e.end}`);
      }
      lines.push("END:VEVENT");
    });
    lines.push("END:VCALENDAR");
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(itin.title || "trip").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Calendar file downloaded");
  };
  const hasVisibleSupplierLinks = (itin?.supplier_links || []).length > 0;
  const isBooked = status === "Booked";
  const isDepositPaid = status === "Deposit Paid" || status === "Deposit Confirmed";
  const isPaidInFull = status === "Paid in Full" || status === "Payment Received";
  const isAuthReceived = status === "Payment Authorization Received";
  const isPaymentLinkSent = status === "Payment Link Sent";
  const supplierLink = (itin?.pricing?.supplier_payment_link || itin?.pricing?.external_payment_link || "").trim();
  const supplierName = (itin?.pricing?.supplier_name || "").trim();
  const externalPayLink = supplierLink; // alias for legacy code paths below

  // Pricing numbers
  const pricing = itin.pricing || {};
  const currency = pricing.currency || "USD";
  const itemTotal = (pricing.items || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const total = parseFloat(pricing.total) || itemTotal || 0;
  const depositAmount = parseFloat(pricing.deposit) || 0;
  const hasDeposit = depositAmount > 0 && depositAmount < total;
  const fmtCur = (amt) => `${currency} ${(parseFloat(amt) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Paid info (from payment_auth)
  const pa = itin.payment_auth || {};
  const balanceDue = parseFloat(pa.balance_due) || Math.max(0, total - depositAmount);
  const balanceDueDate = pa.balance_due_date || (itin.pricing || {}).balance_due_date || "";
  const balancePayLink = pa.balance_payment_link || pricing.balance_payment_link || supplierLink;

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/itineraries/${token}/approve`);
      toast.success("Itinerary approved");
      await loadItinerary();
    } catch { toast.error("Could not approve right now. Please try again."); }
    finally { setApproving(false); }
  };

  const submitDecline = async (e) => {
    e.preventDefault();
    setDeclining(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/itineraries/${token}/decline`, { reason: declineReason });
      toast.success("Your feedback has been sent to your travel designer");
      setDeclineOpen(false);
      setDeclineReason("");
      await loadItinerary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit feedback");
    }
    finally { setDeclining(false); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/itineraries/${token}/payment-auth`, {
        ...payForm,
        payment_type: paymentType,
      });
      toast.success("Authorization received — your travel designer will confirm shortly");
      setPayOpen(false);
      await loadItinerary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit authorization. Please try again.");
    }
    finally { setPaySubmitting(false); }
  };

  const openSupplierLink = async () => {
    if (!supplierLink) return;
    // Open immediately to keep popup-blocker happy, then track in background
    const win = window.open(supplierLink, "_blank", "noopener,noreferrer");
    try {
      await axios.post(`${BACKEND_URL}/api/public/itineraries/${token}/track-supplier-pay`);
      // Refresh status to "Payment Link Sent"
      loadItinerary();
    } catch {
      // best-effort — link is already open
    }
    if (!win) {
      // popup blocked — fall back to top-level navigation
      window.location.href = supplierLink;
    }
  };

  const submitSupplierAuth = async (e) => {
    e.preventDefault();
    if (!supplierAuthTarget) return;
    setSupplierAuthSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/itineraries/${token}/supplier-auth`, {
        ...supplierAuthForm,
        supplier_id: supplierAuthTarget.id,
      });
      toast.success("Authorization received — your travel designer will confirm shortly");
      setSupplierAuthTarget(null);
      await loadItinerary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit authorization. Please try again.");
    } finally {
      setSupplierAuthSubmitting(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!review.testimonial.trim()) { toast.error("Please share a few words"); return; }
    setReviewSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/itineraries/${token}/reviews`, {
        rating: review.rating,
        testimonial: review.testimonial,
        client_name: review.client_name,
        agent_name: itin.agent?.name || "",
        trip_name: itin.title,
        allow_public: review.allow_public,
      });
      toast.success("Thank you for your review");
      setReviewOpen(false);
      setReviewSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    }
    finally { setReviewSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-ink text-white font-sans pb-40">
      {/* Hero */}
      <div className="relative h-[85vh] min-h-[560px] w-full overflow-hidden">
        <img src={itin.cover_image_url} alt={itin.title} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
        <div className="absolute inset-0 grain" />

        <div className="relative z-10 pt-8 px-6">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-sans">Best Time Travel &amp; Tours</p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 pb-12">
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/70 mb-3 font-sans" data-testid="client-destination">
            <MapPin size={12} weight="duotone" className="inline mr-1.5 -mt-0.5" />
            {itin.destination || "Your Journey"}
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] mb-4" data-testid="client-title">
            {itin.title}
          </h1>
          {itin.client_name && (
            <p className="font-serif italic text-xl text-[#D4AF37]" data-testid="client-name">
              Prepared for {itin.client_name}
            </p>
          )}
          {(itin.start_date || itin.end_date) && (
            <p className="mt-4 inline-flex items-center gap-2 text-xs text-white/70 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5">
              <CalendarBlank size={13} weight="duotone" /> {formatDate(itin.start_date)} → {formatDate(itin.end_date)}
            </p>
          )}
          {isBooked && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-green-300 bg-green-500/10 border border-green-400/40 rounded-full px-4 py-1.5" data-testid="booked-badge">
              <CheckCircle size={14} weight="fill" /> Booked &amp; confirmed
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-14">
        {itin.description && (
          <div className="mb-14 text-center">
            <p className="font-serif italic text-xl sm:text-2xl text-white/85 leading-relaxed" data-testid="client-description">
              "{itin.description}"
            </p>
          </div>
        )}

        {/* Thank-you banner after approval */}
        {status === "Approved" && !isBooked && (
          <div className="mb-10 border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-2xl p-6 text-center animate-fade-up" data-testid="approved-banner">
            <Sparkle size={24} weight="duotone" className="text-[#D4AF37] mx-auto mb-2" />
            <p className="font-serif text-2xl text-white mb-1">Thank you!</p>
            <p className="text-white/70 text-sm">Your travel designer will contact you to finalize booking and payment.</p>
          </div>
        )}

        {/* Declined banner */}
        {isDeclined && (
          <div className="mb-10 border border-amber-400/40 bg-amber-500/10 rounded-2xl p-6 text-center animate-fade-up" data-testid="declined-banner">
            <ChatCircleText size={24} weight="duotone" className="text-amber-300 mx-auto mb-2" />
            <p className="font-serif text-2xl text-white mb-1">Feedback received</p>
            <p className="text-white/70 text-sm">Your travel designer has been notified and will get back to you with revisions.</p>
          </div>
        )}

        {/* Trip Summary card */}
        {(itin.start_date || itin.destination || totalPrice) && (
          <section className="mb-12 grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fade-up" data-testid="trip-summary">
            <div className="rounded-xl border border-[#D4AF37]/20 bg-ink-surface p-4">
              <p className="text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1.5 font-sans">Dates</p>
              <p className="font-serif text-base text-white leading-tight">{formatDate(itin.start_date)}</p>
              <p className="font-serif text-base text-white/60 leading-tight">→ {formatDate(itin.end_date)}</p>
              {tripDays && (
                <p className="text-[10px] text-[#D4AF37] mt-1.5 font-sans">
                  {tripDays} day{tripDays > 1 ? "s" : ""}{tripNights != null && tripNights > 0 ? ` · ${tripNights} night${tripNights > 1 ? "s" : ""}` : ""}
                </p>
              )}
            </div>
            <div className="rounded-xl border border-[#D4AF37]/20 bg-ink-surface p-4">
              <p className="text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1.5 font-sans">Travelers</p>
              <p className="font-serif text-2xl text-white">{itin.traveler_count || "—"}</p>
              <p className="text-[10px] text-white/40 mt-1 font-sans">Guest{(itin.traveler_count || 1) === 1 ? "" : "s"}</p>
            </div>
            <div className="rounded-xl border border-[#D4AF37]/20 bg-ink-surface p-4">
              <p className="text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1.5 font-sans">Destination</p>
              <p className="font-serif text-base text-white leading-tight line-clamp-2">{itin.destination || "—"}</p>
            </div>
            <div className="rounded-xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/15 to-transparent p-4">
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1.5 font-sans">Investment</p>
              <p className="font-serif text-2xl text-[#D4AF37] leading-tight">{totalPrice ? fmtCur(totalPrice) : "—"}</p>
              <p className="text-[10px] text-white/50 mt-1 font-sans uppercase tracking-widest">{(itin.pricing || {}).currency || "USD"}</p>
            </div>
          </section>
        )}

        {/* Convenience toolbar */}
        {canDownloadPdf && (
          <section className="mb-12 flex flex-wrap items-center gap-2 animate-fade-up" data-testid="client-toolbar">
            <Button variant="outline" onClick={copyShareLink} className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-10 text-[11px] uppercase tracking-widest" data-testid="copy-link-btn">
              <ArrowSquareOut size={14} weight="duotone" className="mr-2 rotate-45" /> Copy link
            </Button>
            <Button variant="outline" onClick={downloadCalendar} className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-10 text-[11px] uppercase tracking-widest" data-testid="save-calendar-btn">
              <CalendarBlank size={14} weight="duotone" className="mr-2" /> Save to calendar
            </Button>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); downloadPdf(); }} className="inline-flex">
              <Button variant="outline" disabled={pdfDownloading} className="border-[#D4AF37]/40 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 h-10 text-[11px] uppercase tracking-widest" data-testid="pdf-download-btn">
                {pdfDownloading ? <><CircleNotch size={14} className="mr-2 animate-spin" /> Generating…</> : <><DownloadSimple size={14} weight="duotone" className="mr-2" /> Download PDF</>}
              </Button>
            </a>
          </section>
        )}

        {/* DAY BY DAY TIMELINE — auto-organized from item dates (flights,
            hotels, tours, transportation) PLUS any agent-placed day items.
            Day cards are generated from start_date → end_date so every trip
            day is represented even if the agent didn't fill it in. */}
        {(() => {
          const timeline = buildDayTimeline(itin);
          const hasAnyItems = timeline.some((d) => (d.items || []).length > 0);
          if (timeline.length === 0 || !hasAnyItems) return null;
          return (
            <section className="mb-14" data-testid="client-days">
              <SectionLabel icon={CalendarBlank} label="Day by Day" />
              <div className="space-y-5">
                {timeline.map((d, i) => (
                  <DayCard key={d.date || i} day={d} />
                ))}
              </div>
            </section>
          );
        })()}

        {/* What's Included / Not Included */}
        {(inclusions.length > 0 || exclusions.length > 0) && (
          <section className="mb-14 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="client-trust">
            {inclusions.length > 0 && (
              <div className="border border-[#D4AF37]/30 bg-ink-surface rounded-2xl p-6" data-testid="inclusions-block">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">What's Included</p>
                <ul className="space-y-2.5">
                  {inclusions.map((x, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/85 font-sans">
                      <CheckCircle size={16} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {exclusions.length > 0 && (
              <div className="border border-white/10 bg-ink-surface rounded-2xl p-6" data-testid="exclusions-block">
                <p className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-3 font-sans">Not Included</p>
                <ul className="space-y-2.5">
                  {exclusions.map((x, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/65 font-sans">
                      <X size={16} weight="bold" className="text-white/30 shrink-0 mt-0.5" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Terms & Notes */}
        {itin.terms && (
          <section className="mb-14" data-testid="client-terms">
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-3 font-sans">Terms &amp; Notes</p>
            <div className="border-l-2 border-[#D4AF37]/40 bg-ink-surface/60 rounded-r-lg p-5 whitespace-pre-wrap text-white/75 text-sm leading-relaxed font-sans">
              {itin.terms}
            </div>
          </section>
        )}

        {/* Agent Contact Card */}
        {agentCard.email && (
          <section className="mb-14" data-testid="client-agent-card">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Your travel designer</p>
            <div className="border border-[#D4AF37]/25 bg-ink-surface rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                {agentCard.avatar_url ? (
                  <img src={agentCard.avatar_url} alt={agentCard.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <span className="font-serif text-xl text-[#D4AF37]">{(agentCard.name || "?").charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-xl text-white">{agentCard.name || "Your travel designer"}</p>
                <p className="text-xs text-[#D4AF37] uppercase tracking-widest font-sans mb-2">{agentCard.title || "Travel Designer"}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-white/70 font-sans">
                  {agentCard.email && (
                    <a href={`mailto:${agentCard.email}`} className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors" data-testid="agent-email-link">
                      <Envelope size={12} weight="duotone" /> {agentCard.email}
                    </a>
                  )}
                  {agentCard.phone && (
                    <a href={`tel:${agentCard.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors" data-testid="agent-phone-link">
                      <Phone size={12} weight="duotone" /> {agentCard.phone}
                    </a>
                  )}
                </div>
              </div>
              <Button onClick={() => window.location.href = `mailto:${agentCard.email}?subject=Question about ${encodeURIComponent(itin.title)}`} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 px-5 text-[11px] uppercase tracking-widest" data-testid="agent-contact-btn">
                <ChatCircleText size={14} weight="duotone" className="mr-2" /> Message
              </Button>
            </div>
          </section>
        )}

        {/* Payment Authorization Received — intent recorded, awaiting agent confirmation */}
        {isAuthReceived && !isBooked && (
          <div className="mb-10 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded-2xl p-6 animate-fade-up" data-testid="auth-received-banner">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={22} weight="duotone" className="text-[#D4AF37]" />
              <p className="font-serif text-2xl text-white">Authorization received</p>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Thank you. Your travel designer will process the {((itin.payment_auth || {}).payment_type === "deposit") ? "deposit" : "payment"} via our secure system and confirm shortly.
            </p>
          </div>
        )}

        {/* Payment Link Sent — client opened supplier link, agent awaiting confirmation */}
        {isPaymentLinkSent && !isBooked && (
          <div className="mb-10 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded-2xl p-6 animate-fade-up" data-testid="link-sent-banner">
            <div className="flex items-center gap-3 mb-2">
              <ArrowSquareOut size={22} weight="duotone" className="text-[#D4AF37]" />
              <p className="font-serif text-2xl text-white">Complete payment with supplier</p>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              You&rsquo;ve started checkout with {supplierName || "the supplier"}. Once your payment is received, your travel designer will confirm your deposit or full payment here.
            </p>
            {supplierLink && (
              <Button onClick={openSupplierLink} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 text-xs uppercase tracking-widest" data-testid="reopen-supplier-link">
                <ArrowSquareOut size={16} weight="duotone" /> Reopen supplier link
              </Button>
            )}
          </div>
        )}

        {/* Deposit paid — show balance due */}
        {isDepositPaid && !isBooked && (
          <div className="mb-10 border border-emerald-400/40 bg-emerald-500/5 rounded-2xl p-6 animate-fade-up" data-testid="deposit-paid-banner">
            <div className="flex items-center gap-3 mb-3">
              <Sparkle size={22} weight="duotone" className="text-emerald-300" />
              <p className="font-serif text-2xl text-white">Deposit confirmed — thank you!</p>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/50">Remaining balance</p>
                <p className="font-serif text-xl text-white">{fmtCur(balanceDue)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/50">Due by</p>
                <p className="font-serif text-xl text-[#D4AF37]">{balanceDueDate ? formatDate(balanceDueDate) : "—"}</p>
              </div>
            </div>
            {balancePayLink && (
              <a href={balancePayLink} target="_blank" rel="noopener noreferrer" className="block" data-testid="balance-pay-link">
                <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white text-xs uppercase tracking-widest">
                  <ArrowSquareOut size={16} weight="duotone" /> Pay remaining balance
                </Button>
              </a>
            )}
            <p className="text-xs text-white/50 mt-3 text-center">We'll send reminders before the due date.</p>
          </div>
        )}

        {/* Paid in full — simple thank you */}
        {isPaidInFull && !isBooked && (
          <div className="mb-10 border border-emerald-400/40 bg-emerald-500/10 rounded-2xl p-6 text-center animate-fade-up" data-testid="paid-full-banner">
            <Sparkle size={24} weight="duotone" className="text-emerald-300 mx-auto mb-2" />
            <p className="font-serif text-2xl text-white mb-1">Payment received in full — thank you!</p>
            <p className="text-white/70 text-sm">Your travel designer will confirm your booking shortly.</p>
          </div>
        )}

        {/* FLIGHTS */}
        {hasAny("flights") && (
          <section className="mb-14" data-testid="client-flights">
            <SectionLabel icon={Airplane} label="Flights" />
            <div className="space-y-4">
              {itin.flights.map((f) => {
                // Backward-compatible date/time read: prefer split fields,
                // fall back to the legacy `departure`/`arrival` datetime strings.
                const depDate = f.departure_date || f.departure || "";
                const depTime = f.departure_time || (f.departure ? f.departure : "");
                const arrDate = f.arrival_date || f.arrival || "";
                const arrTime = f.arrival_time || (f.arrival ? f.arrival : "");
                const priceLine = f.price ? `${f.currency || ""} ${f.price}`.trim() : "";
                return (
                <div key={f.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl p-5" data-testid={`client-flight-${f.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-serif text-xl text-white">
                        {f.airline || "Airline"}
                        {f.flight_number && <span className="text-[#D4AF37] font-sans text-base ml-2">{f.flight_number}</span>}
                      </p>
                      <p className="text-xs text-white/50">{f.cabin_class || ""}</p>
                    </div>
                    <Airplane size={22} weight="duotone" className="text-[#D4AF37]" />
                  </div>
                  <div className="flex items-stretch gap-4">
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-widest text-white/40">{f.from_airport}</p>
                      <p className="font-serif text-2xl text-white">{f.from_city || f.from_airport || "—"}</p>
                      <p className="text-xs text-white/60 mt-1">{formatDate(depDate)}</p>
                      <p className="text-sm text-[#D4AF37]">{formatTime(depTime)}</p>
                    </div>
                    <div className="flex flex-col items-center justify-center text-white/30">
                      <div className="h-px w-16 bg-[#D4AF37]/40" />
                      <Airplane size={14} className="text-[#D4AF37] my-1" weight="fill" />
                    </div>
                    <div className="flex-1 text-right">
                      <p className="text-[10px] uppercase tracking-widest text-white/40">{f.to_airport}</p>
                      <p className="font-serif text-2xl text-white">{f.to_city || f.to_airport || "—"}</p>
                      <p className="text-xs text-white/60 mt-1">{formatDate(arrDate)}</p>
                      <p className="text-sm text-[#D4AF37]">
                        {formatTime(arrTime)}
                        {dayOffsetLabel(depDate, arrDate) && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[9px] font-sans uppercase tracking-wider align-middle">
                            {dayOffsetLabel(depDate, arrDate)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {(f.confirmation_number || priceLine || f.link) && (
                    <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/60">
                      {f.confirmation_number && (
                        <span className="flex items-center gap-1.5">
                          <SealCheck size={14} weight="duotone" className="text-[#D4AF37]" />
                          Confirmation · <span className="text-white font-mono">{f.confirmation_number}</span>
                        </span>
                      )}
                      {priceLine && <span className="text-white/70">{priceLine}</span>}
                      {f.link && (
                        <a href={f.link} target="_blank" rel="noopener noreferrer"
                           className="text-[#D4AF37] hover:underline" data-testid={`client-flight-link-${f.id}`}>
                          View document →
                        </a>
                      )}
                    </div>
                  )}
                  {f.notes && <p className="mt-2 text-xs text-white/50 italic">{f.notes}</p>}
                </div>
              );})}
            </div>
          </section>
        )}

        {/* HOTELS */}
        {hasAny("hotels") && (
          <section className="mb-14" data-testid="client-hotels">
            <SectionLabel icon={BuildingApartment} label="Hotels" />
            <div className="space-y-6">
              {itin.hotels.map((h) => {
                const addressLine = h.address || h.location || "";
                const checkInLabel = `${formatDate(h.check_in)}${h.check_in_time ? ` · ${formatTime(h.check_in_time)}` : ""}`;
                const checkOutLabel = `${formatDate(h.check_out)}${h.check_out_time ? ` · ${formatTime(h.check_out_time)}` : ""}`;
                const priceLine = h.price ? `${h.currency || ""} ${h.price}`.trim() : "";
                return (
                <div key={h.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl overflow-hidden" data-testid={`client-hotel-${h.id}`}>
                  {h.image_url && <div className="aspect-[16/10] overflow-hidden"><img src={h.image_url} alt={h.name} className="w-full h-full object-cover" /></div>}
                  <div className="p-5">
                    {addressLine && <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5">{addressLine}</p>}
                    <h3 className="font-serif text-2xl text-white mb-3">{h.name}</h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Check-in</p>
                        <p className="text-white">{checkInLabel}</p>
                      </div>
                      <div>
                        <p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Check-out</p>
                        <p className="text-white">{checkOutLabel}</p>
                      </div>
                      {h.room_type && (
                        <div className="col-span-2">
                          <p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Room</p>
                          <p className="text-white">{h.room_type}</p>
                        </div>
                      )}
                    </div>
                    {(h.confirmation_number || priceLine || h.link) && (
                      <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/60">
                        {h.confirmation_number && (
                          <span className="flex items-center gap-1.5">
                            <SealCheck size={14} weight="duotone" className="text-[#D4AF37]" />
                            <span className="text-white font-mono">{h.confirmation_number}</span>
                          </span>
                        )}
                        {priceLine && <span className="text-white/70">{priceLine}</span>}
                        {h.link && (
                          <a href={h.link} target="_blank" rel="noopener noreferrer"
                             className="text-[#D4AF37] hover:underline" data-testid={`client-hotel-link-${h.id}`}>
                            View document →
                          </a>
                        )}
                      </div>
                    )}
                    {h.notes && <p className="mt-2 text-xs text-white/50 italic">{h.notes}</p>}
                  </div>
                </div>
              );})}
            </div>
          </section>
        )}

        {/* TOURS */}
        {hasAny("tours") && (
          <section className="mb-14" data-testid="client-tours">
            <SectionLabel icon={MapTrifold} label="Tours & Experiences" />
            <div className="space-y-6">
              {itin.tours.map((t) => (
                <div key={t.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl overflow-hidden">
                  {t.image_url && <div className="aspect-[16/10] overflow-hidden"><img src={t.image_url} alt={t.title} className="w-full h-full object-cover" /></div>}
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5">{t.location}</p>
                    <h3 className="font-serif text-2xl text-white mb-3">{t.title}</h3>
                    <div className="flex flex-wrap gap-3 text-xs text-white/70 mb-3">
                      {t.date && <span className="flex items-center gap-1"><CalendarBlank size={12} weight="duotone" /> {formatDate(t.date)}</span>}
                      {t.duration && <span className="flex items-center gap-1"><Clock size={12} weight="duotone" /> {t.duration}</span>}
                      {t.provider && <span className="text-white/50">by {t.provider}</span>}
                    </div>
                    {t.description && <p className="text-sm text-white/70 leading-relaxed">{t.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CRUISE */}
        {hasAny("cruises") && (
          <section className="mb-14" data-testid="client-cruises">
            <SectionLabel icon={Boat} label="Cruise" />
            <div className="space-y-4">
              {itin.cruises.map((c) => (
                <div key={c.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl overflow-hidden">
                  {c.image_url && <div className="aspect-[16/9] overflow-hidden"><img src={c.image_url} alt={c.ship_name} className="w-full h-full object-cover" /></div>}
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1.5">{c.line_name}</p>
                    <h3 className="font-serif text-2xl text-white mb-3">{c.ship_name}</h3>
                    <div className="flex items-stretch gap-4">
                      <div className="flex-1"><p className="text-[10px] uppercase tracking-widest text-white/40">From</p><p className="font-serif text-xl text-white">{c.from_port}</p><p className="text-xs text-[#D4AF37] mt-1">{formatDate(c.departure)} {formatTime(c.departure)}</p></div>
                      <div className="flex flex-col items-center justify-center"><Boat size={18} weight="duotone" className="text-[#D4AF37]" /></div>
                      <div className="flex-1 text-right"><p className="text-[10px] uppercase tracking-widest text-white/40">To</p><p className="font-serif text-xl text-white">{c.to_port}</p><p className="text-xs text-[#D4AF37] mt-1">{formatDate(c.arrival)} {formatTime(c.arrival)}</p></div>
                    </div>
                    {c.cabin_type && <p className="mt-3 text-xs text-white/70">Cabin: {c.cabin_type}</p>}
                    {c.notes && <p className="mt-2 text-xs text-white/50 italic">{c.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* TRANSPORTATION */}
        {hasAny("transportation") && (
          <section className="mb-14" data-testid="client-transportation">
            <SectionLabel icon={Car} label="Transportation" />
            <div className="space-y-4">
              {itin.transportation.map((t) => (
                <div key={t.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl overflow-hidden">
                  {t.image_url && <div className="aspect-[16/9] overflow-hidden"><img src={t.image_url} alt={t.vehicle} className="w-full h-full object-cover" /></div>}
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{t.type}</p>
                        <h3 className="font-serif text-2xl text-white">{t.vehicle || t.provider || "Transfer"}</h3>
                        {t.provider && t.vehicle && <p className="text-xs text-white/50 mt-0.5">{t.provider}</p>}
                      </div>
                      <Car size={22} weight="duotone" className="text-[#D4AF37]" />
                    </div>
                    <div className="flex items-stretch gap-4 mt-2">
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-widest text-white/40">Pickup</p>
                        <p className="text-white text-sm">{t.pickup_location || "—"}</p>
                        {t.pickup_datetime && <p className="text-xs text-[#D4AF37] mt-1">{formatDate(t.pickup_datetime)} {formatTime(t.pickup_datetime)}</p>}
                      </div>
                      <div className="flex flex-col items-center justify-center text-white/30"><div className="h-px w-10 bg-[#D4AF37]/40" /></div>
                      <div className="flex-1 text-right">
                        <p className="text-[10px] uppercase tracking-widest text-white/40">Drop-off</p>
                        <p className="text-white text-sm">{t.dropoff_location || "—"}</p>
                        {t.dropoff_datetime && <p className="text-xs text-[#D4AF37] mt-1">{formatDate(t.dropoff_datetime)} {formatTime(t.dropoff_datetime)}</p>}
                      </div>
                    </div>
                    {t.confirmation_number && <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-xs text-white/60"><SealCheck size={14} weight="duotone" className="text-[#D4AF37]" /> {t.confirmation_number}</div>}
                    {t.notes && <p className="mt-2 text-xs text-white/50 italic">{t.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* TRAVEL PROTECTION */}
        {hasAny("protection") && (
          <section className="mb-14" data-testid="client-protection">
            <SectionLabel icon={ShieldCheck} label="Travel Protection" />
            <div className="space-y-4">
              {itin.protection.map((p) => (
                <div key={p.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{p.provider || "Insurance"}</p>
                      <h3 className="font-serif text-2xl text-white">{p.plan_name || "Protection Plan"}</h3>
                    </div>
                    <ShieldCheck size={22} weight="duotone" className="text-[#D4AF37]" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {p.policy_number && <div><p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Policy</p><p className="text-white font-mono">{p.policy_number}</p></div>}
                    {p.coverage_amount && <div><p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Coverage</p><p className="text-white">{p.coverage_amount}</p></div>}
                    {p.start_date && <div><p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Starts</p><p className="text-white">{formatDate(p.start_date)}</p></div>}
                    {p.end_date && <div><p className="text-white/40 uppercase tracking-widest text-[10px] mb-0.5">Ends</p><p className="text-white">{formatDate(p.end_date)}</p></div>}
                  </div>
                  {p.coverage_details && <p className="mt-4 pt-4 border-t border-white/5 text-xs text-white/70 leading-relaxed">{p.coverage_details}</p>}
                  {p.emergency_contact && <div className="mt-3 flex items-center gap-2 text-xs"><Phone size={14} weight="duotone" className="text-[#D4AF37]" /><span className="text-white/50">24/7 Emergency:</span><span className="text-white">{p.emergency_contact}</span></div>}
                  {p.notes && <p className="mt-2 text-xs text-white/50 italic">{p.notes}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* PRICING */}
        {(itin.pricing?.items?.length > 0 || itin.pricing?.total > 0) && (
          <section className="mb-14" data-testid="client-pricing">
            <SectionLabel icon={CurrencyDollar} label="Investment" />
            <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-2xl p-6">
              {(itin.pricing.items || []).map((p) => (
                <div key={p.id} className="flex justify-between items-center py-3 border-b border-white/5 text-sm">
                  <span className="text-white/80">{p.label || "Item"}</span>
                  <span className="text-white font-mono">
                    {itin.pricing.currency} {(parseFloat(p.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-5 mt-2">
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Total</span>
                <span className="font-serif text-3xl text-white">
                  {itin.pricing.currency} {(parseFloat(itin.pricing.total) || (itin.pricing.items || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              {itin.pricing.deposit > 0 && (
                <p className="text-xs text-white/60 mt-2">
                  Deposit due: <span className="text-[#D4AF37]">{itin.pricing.currency} {parseFloat(itin.pricing.deposit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </p>
              )}
              {itin.pricing.notes && <p className="mt-4 pt-4 border-t border-white/5 text-xs text-white/60 italic leading-relaxed">{itin.pricing.notes}</p>}
            </div>

            {/* Secure-payment guidance — shown anytime payment is relevant */}
            {(canPay || isAuthReceived || isPaymentLinkSent) && (
              <div className="mt-4 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5 flex gap-3 items-start" data-testid="supplier-payment-note">
                <ShieldCheck size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-white/75 leading-relaxed">
                  Some payments may be processed directly through the travel supplier&rsquo;s secure payment system.
                  <span className="text-white/90"> Best Time Travel &amp; Tours</span> will guide you through the safest payment option for your booking.
                </p>
              </div>
            )}
          </section>
        )}

        {/* COMPLETE YOUR BOOKING — guided supplier checklist (replaces auth form when supplier links exist) */}
        {(itin.supplier_links || []).length > 0 && (canPay || isAuthReceived || isPaymentLinkSent || isDepositPaid) && (
          <section className="mb-14" data-testid="client-supplier-links">
            <SectionLabel icon={CreditCard} label="Complete your booking" />
            <p className="text-center text-white/60 text-sm mb-6 max-w-xl mx-auto">
              Pay each item below in your own time. We've ordered them by priority — start at the top.
            </p>
            <ol className="space-y-3">
              {(itin.supplier_links || []).map((sl, idx) => (
                <PayChecklistItem
                  key={sl.id}
                  sl={sl}
                  index={idx}
                  onAuthRequest={(supplier) => {
                    setSupplierAuthTarget(supplier);
                    setSupplierAuthForm({ cardholder_name: "", card_last4: "", card_exp: "", billing_zip: "", signature: "", notes: "" });
                  }}
                />
              ))}
            </ol>
            <p className="text-[11px] text-white/40 mt-4 italic text-center">
              Each "Pay Now" button opens the supplier's secure payment page in a new window. Best Time Travel &amp; Tours will guide you if you have any questions.
            </p>
          </section>
        )}

        {/* Booked — download travel documents */}
        {isBooked && (
          <section className="mb-10 border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 via-ink-surface to-ink-surface rounded-2xl p-7 text-center animate-fade-up" data-testid="download-docs-section">
            <DownloadSimple size={28} weight="duotone" className="text-[#D4AF37] mx-auto mb-2" />
            <h3 className="font-serif text-3xl text-white mb-2">Your journey is confirmed</h3>
            <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
              Download your complete travel documents — flights, hotels, tours, and everything you need in one luxury PDF.
            </p>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.preventDefault(); downloadPdf(); }}
              data-testid="download-pdf-link"
            >
              <Button disabled={pdfDownloading} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] h-12 px-8">
                {pdfDownloading ? <><CircleNotch size={18} className="animate-spin" /> Generating…</> : <><DownloadSimple size={18} weight="duotone" /> Download Your Travel Documents</>}
              </Button>
            </a>
          </section>
        )}

        {/* Review prompt after booking */}
        {isBooked && !reviewSubmitted && (
          <section className="mb-14 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded-2xl p-8 text-center" data-testid="review-prompt">
            <Star size={28} weight="duotone" className="text-[#D4AF37] mx-auto mb-2" />
            <h3 className="font-serif text-3xl text-white mb-2">How was your journey?</h3>
            <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
              Your reflections help us craft better experiences — and help future travelers choose with confidence.
            </p>
            <Button
              onClick={() => setReviewOpen(true)}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] h-11 px-6"
              data-testid="open-review-btn"
            >
              <Star size={16} weight="fill" /> Share your experience
            </Button>
          </section>
        )}

        {reviewSubmitted && (
          <section className="mb-14 border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-2xl p-8 text-center" data-testid="review-thankyou">
            <Sparkle size={24} weight="duotone" className="text-[#D4AF37] mx-auto mb-2" />
            <p className="font-serif text-2xl text-white mb-1">Thank you for sharing.</p>
            <p className="text-white/70 text-sm">Your review is pending approval and will appear on our testimonials page shortly.</p>
          </section>
        )}

        {/* Agent footer — slim brand-only signoff (full agent card already shown above) */}
        {itin.agent && (
          <div className="text-center pt-10 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/30 font-sans">
              Best Time Travel &amp; Tours
            </p>

            {/* Trade association affiliations */}
            <div className="mt-7 flex items-center justify-center gap-5 sm:gap-8 flex-wrap" data-testid="trade-affiliations">
              <div className="flex flex-col items-center gap-1.5" data-testid="asta-badge">
                <div className="px-5 py-2 border border-white/15 rounded-md bg-black/20 hover:border-[#D4AF37]/40 transition-colors">
                  <span className="font-serif text-2xl tracking-[0.18em] text-white/85">ASTA</span>
                </div>
                <span className="text-[9px] uppercase tracking-[0.3em] text-white/40 font-sans">Member</span>
              </div>

              <div className="h-12 w-px bg-white/10" aria-hidden />

              <div className="flex flex-col items-center gap-1.5" data-testid="ccra-badge">
                <div className="px-5 py-2 border border-white/15 rounded-md bg-black/20 hover:border-[#D4AF37]/40 transition-colors">
                  <span className="font-serif text-2xl tracking-[0.18em] text-white/85">CCRA</span>
                </div>
                <span className="text-[9px] uppercase tracking-[0.3em] text-white/40 font-sans">Affiliate</span>
              </div>

              <div className="h-12 w-px bg-white/10" aria-hidden />

              <div className="flex flex-col items-center gap-1.5" data-testid="true-badge">
                <div className="px-5 py-2 border border-white/15 rounded-md bg-black/20 hover:border-[#D4AF37]/40 transition-colors">
                  <span className="font-serif text-2xl tracking-[0.18em] text-white/85">TRUE</span>
                </div>
                <span className="text-[9px] uppercase tracking-[0.3em] text-white/40 font-sans">Accredited</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom action bar */}
      {(canApprove || canPay || canDownloadPdf) && (
        <div className="fixed bottom-0 left-0 right-0 z-30 glass" data-testid="client-action-bar">
          <div className="max-w-3xl mx-auto px-5 py-4 flex flex-col sm:flex-row gap-3">
            {canApprove && (
              <Button
                onClick={handleApprove}
                disabled={approving}
                className="flex-1 h-14 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.3em] font-medium"
                data-testid="approve-itinerary-btn"
              >
                {approving ? <CircleNotch size={18} className="animate-spin" /> : <><CheckCircle size={18} weight="duotone" /> Approve Proposal</>}
              </Button>
            )}
            {canDecline && (
              <Button
                variant="outline"
                onClick={() => setDeclineOpen(true)}
                className="h-14 border-white/20 bg-transparent text-white/80 hover:bg-white/5 hover:text-white hover:border-white/40 text-xs uppercase tracking-[0.3em] sm:flex-none sm:px-6"
                data-testid="decline-itinerary-btn"
              >
                <ChatCircleText size={18} weight="duotone" /> Request Changes
              </Button>
            )}
            {canPay && (
              <>
                {hasVisibleSupplierLinks ? (
                  // PRIMARY PATH: scroll to "Complete Your Booking" checklist
                  <Button
                    onClick={() => {
                      const el = document.querySelector('[data-testid="client-supplier-links"]');
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="flex-1 h-14 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.3em] font-medium"
                    data-testid="complete-booking-btn"
                  >
                    <CreditCard size={18} weight="duotone" /> Complete Your Booking
                  </Button>
                ) : supplierLink ? (
                  <Button
                    onClick={openSupplierLink}
                    className="flex-1 h-14 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.3em] font-medium"
                    data-testid="pay-supplier-btn"
                  >
                    <ArrowSquareOut size={18} weight="duotone" /> Pay Securely with Supplier
                  </Button>
                ) : (
                  // BACKUP PATH: only when no supplier links AND no single supplier_payment_link
                  <Button
                    onClick={() => setPayOpen(true)}
                    className="flex-1 h-14 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.3em] font-medium"
                    data-testid="open-payment-btn"
                  >
                    <CreditCard size={18} weight="duotone" /> Submit Payment Authorization
                  </Button>
                )}
                {!hasVisibleSupplierLinks && supplierLink && (
                  <Button
                    variant="outline"
                    onClick={() => setPayOpen(true)}
                    className="h-14 border-white/15 bg-transparent text-white/70 hover:bg-white/5 hover:text-white text-xs uppercase tracking-[0.3em] sm:flex-none sm:px-5"
                    data-testid="open-backup-auth-btn"
                  >
                    <CreditCard size={16} weight="duotone" /> <span className="hidden sm:inline">Backup auth form</span><span className="sm:hidden">Backup auth</span>
                  </Button>
                )}
              </>
            )}
            {canDownloadPdf && !canApprove && !canPay && (
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); downloadPdf(); }} className="flex-1">
                <Button disabled={pdfDownloading} className="w-full h-14 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.3em]" data-testid="sticky-pdf-btn">
                  {pdfDownloading ? <><CircleNotch size={18} className="animate-spin" /> Generating…</> : <><DownloadSimple size={18} weight="duotone" /> Download PDF</>}
                </Button>
              </a>
            )}
            {canDownloadPdf && (canApprove || canPay) && (
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); downloadPdf(); }} className="hidden sm:block">
                <Button variant="outline" disabled={pdfDownloading} className="h-14 border-white/15 bg-transparent text-white/70 hover:bg-white/5 hover:text-white text-xs uppercase tracking-[0.3em] px-5" data-testid="sticky-pdf-secondary-btn" aria-label="Download PDF">
                  {pdfDownloading ? <CircleNotch size={16} className="animate-spin" /> : <DownloadSimple size={16} weight="duotone" />}
                </Button>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Decline / Request Changes Dialog */}
      <Dialog open={declineOpen} onOpenChange={(o) => { setDeclineOpen(o); if (!o) setDeclineReason(""); }}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Request <span className="italic text-[#D4AF37]">changes</span>
            </DialogTitle>
            <p className="text-xs text-white/50 font-sans mt-1">
              Share your feedback — your travel designer will revise the proposal.
            </p>
          </DialogHeader>
          <form onSubmit={submitDecline} className="space-y-4 pt-2" data-testid="decline-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Your feedback (optional)</Label>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={5}
                placeholder="e.g. Could you swap the second hotel for something closer to the beach?"
                className="bg-black/40 border-white/10 text-white focus:border-[#D4AF37] font-sans"
                data-testid="decline-reason-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDeclineOpen(false)} className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11 flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={declining} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 flex-1 text-xs uppercase tracking-widest" data-testid="submit-decline-btn">
                {declining ? <CircleNotch size={18} className="animate-spin" /> : "Send feedback"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Authorization Dialog */}
      <Dialog open={payOpen} onOpenChange={(o) => { setPayOpen(o); if (o) setPaymentType("full"); }}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Backup Payment <span className="italic text-[#D4AF37]">Authorization</span>
            </DialogTitle>
            <p className="text-xs text-white/60 font-sans mt-1 leading-relaxed">
              Use this form only when the supplier requires manual processing.
              We collect <strong className="text-white/90">authorization details only</strong> — no full card numbers or CVV are stored.
            </p>
          </DialogHeader>

          {/* Payment type selector */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="payment-type-selector">
            <button
              type="button"
              onClick={() => setPaymentType("full")}
              className={`text-left rounded-lg border p-4 transition-all ${paymentType === "full" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}
              data-testid="pay-full-option"
            >
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Option 1</p>
              <p className="font-serif text-lg text-white">Pay in Full</p>
              <p className="font-mono text-sm text-white/80 mt-1">{fmtCur(total)}</p>
            </button>
            <button
              type="button"
              disabled={!hasDeposit}
              onClick={() => hasDeposit && setPaymentType("deposit")}
              className={`text-left rounded-lg border p-4 transition-all ${!hasDeposit ? "border-white/5 bg-black/20 opacity-40 cursor-not-allowed" : paymentType === "deposit" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}
              data-testid="pay-deposit-option"
            >
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Option 2</p>
              <p className="font-serif text-lg text-white">Pay Deposit</p>
              {hasDeposit ? (
                <>
                  <p className="font-mono text-sm text-white/80 mt-1">{fmtCur(depositAmount)}</p>
                  <p className="text-[11px] text-white/50 mt-0.5">Balance: {fmtCur(total - depositAmount)}</p>
                </>
              ) : (
                <p className="text-[11px] text-white/40 mt-1 italic">No deposit option for this trip</p>
              )}
            </button>
          </div>

          {supplierLink && (
            <div className="rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 mb-2">
              <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] mb-2">Faster &amp; safer — pay directly with the supplier{supplierName ? ` (${supplierName})` : ""}</p>
              <Button
                type="button"
                onClick={() => { setPayOpen(false); openSupplierLink(); }}
                className="w-full h-11 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest"
                data-testid="dialog-supplier-link"
              >
                <ArrowSquareOut size={16} weight="duotone" /> Pay Securely with Supplier
              </Button>
              <p className="text-[11px] text-white/40 mt-2 text-center">or use the backup authorization form below</p>
            </div>
          )}

          <form onSubmit={submitPayment} className="space-y-3" data-testid="payment-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Cardholder name</Label>
              <Input required value={payForm.cardholder_name} onChange={(e) => setPayForm({ ...payForm, cardholder_name: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="pay-cardholder" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Card last 4</Label>
                <Input required maxLength={4} placeholder="0000" value={payForm.card_last4} onChange={(e) => setPayForm({ ...payForm, card_last4: e.target.value.replace(/\D/g, "") })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="pay-last4" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Expiry (MM/YY)</Label>
                <Input required placeholder="MM/YY" value={payForm.card_exp} onChange={(e) => setPayForm({ ...payForm, card_exp: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="pay-exp" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Billing ZIP</Label>
              <Input required value={payForm.billing_zip} onChange={(e) => setPayForm({ ...payForm, billing_zip: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="pay-zip" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Signature (type full name)</Label>
              <Input required value={payForm.signature} onChange={(e) => setPayForm({ ...payForm, signature: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37] font-serif italic" data-testid="pay-signature" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Notes (optional)</Label>
              <Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="pay-notes" />
            </div>
            <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-3 text-xs">
              <div className="flex justify-between items-baseline">
                <span className="text-white/60">You authorize charge of:</span>
                <span className="font-serif text-xl text-[#D4AF37]" data-testid="pay-charge-amount">
                  {paymentType === "deposit" ? fmtCur(depositAmount) : fmtCur(total)}
                </span>
              </div>
              {paymentType === "deposit" && (
                <p className="text-[11px] text-white/50 mt-1 italic">Remaining balance of {fmtCur(total - depositAmount)} will be due later.</p>
              )}
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" disabled={paySubmitting} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black w-full h-11 text-xs uppercase tracking-[0.25em]" data-testid="submit-payment-btn">
                {paySubmitting ? <CircleNotch size={18} className="animate-spin" /> : "Submit Authorization"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Per-Supplier Authorization Dialog */}
      <Dialog open={!!supplierAuthTarget} onOpenChange={(o) => { if (!o) setSupplierAuthTarget(null); }}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Submit Payment <span className="italic text-[#D4AF37]">Details</span>
            </DialogTitle>
            <p className="text-xs text-white/60 font-sans mt-1 leading-relaxed">
              For <strong className="text-white">{supplierAuthTarget?.supplier_name || supplierAuthTarget?.category || "this supplier"}</strong>{supplierAuthTarget?.category && supplierAuthTarget?.supplier_name ? ` (${supplierAuthTarget.category})` : ""}.
              We collect <strong className="text-white/90">authorization details only</strong> — no full card numbers or CVV are stored. Your travel designer will process payment manually with the supplier.
            </p>
          </DialogHeader>
          <form onSubmit={submitSupplierAuth} className="space-y-3" data-testid="supplier-auth-form">
            <div>
              <Label htmlFor="sa-cardholder" className="text-[10px] uppercase tracking-widest text-white/50">Cardholder name</Label>
              <Input id="sa-cardholder" required value={supplierAuthForm.cardholder_name} onChange={(e) => setSupplierAuthForm({ ...supplierAuthForm, cardholder_name: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="sa-cardholder" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="sa-last4" className="text-[10px] uppercase tracking-widest text-white/50">Card last 4</Label>
                <Input id="sa-last4" required maxLength={4} pattern="\d{4}" inputMode="numeric" value={supplierAuthForm.card_last4} onChange={(e) => setSupplierAuthForm({ ...supplierAuthForm, card_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="bg-black/40 border-white/10 text-white" data-testid="sa-last4" />
              </div>
              <div>
                <Label htmlFor="sa-exp" className="text-[10px] uppercase tracking-widest text-white/50">Exp MM/YY</Label>
                <Input id="sa-exp" required placeholder="12/27" value={supplierAuthForm.card_exp} onChange={(e) => setSupplierAuthForm({ ...supplierAuthForm, card_exp: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="sa-exp" />
              </div>
              <div>
                <Label htmlFor="sa-zip" className="text-[10px] uppercase tracking-widest text-white/50">Billing ZIP</Label>
                <Input id="sa-zip" required value={supplierAuthForm.billing_zip} onChange={(e) => setSupplierAuthForm({ ...supplierAuthForm, billing_zip: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="sa-zip" />
              </div>
            </div>
            <div>
              <Label htmlFor="sa-signature" className="text-[10px] uppercase tracking-widest text-white/50">Type your full name as signature</Label>
              <Input id="sa-signature" required value={supplierAuthForm.signature} onChange={(e) => setSupplierAuthForm({ ...supplierAuthForm, signature: e.target.value })} className="bg-black/40 border-white/10 text-white italic" data-testid="sa-signature" />
            </div>
            <div>
              <Label htmlFor="sa-notes" className="text-[10px] uppercase tracking-widest text-white/50">Notes (optional)</Label>
              <Textarea id="sa-notes" rows={2} value={supplierAuthForm.notes} onChange={(e) => setSupplierAuthForm({ ...supplierAuthForm, notes: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="sa-notes" />
            </div>
            <DialogFooter className="!justify-stretch">
              <Button type="submit" disabled={supplierAuthSubmitting} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black w-full h-11 text-xs uppercase tracking-[0.25em]" data-testid="submit-supplier-auth-btn">
                {supplierAuthSubmitting ? <CircleNotch size={18} className="animate-spin" /> : "Submit Authorization"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Share your <span className="italic text-[#D4AF37]">experience</span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
            <div className="flex flex-col items-center gap-2 py-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">Your rating</Label>
              <StarPicker value={review.rating} onChange={(n) => setReview({ ...review, rating: n })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Your testimonial</Label>
              <Textarea required rows={4} placeholder="What made this journey special?" value={review.testimonial} onChange={(e) => setReview({ ...review, testimonial: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="review-testimonial" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-1.5 block">Your name (as it will appear)</Label>
              <Input value={review.client_name} onChange={(e) => setReview({ ...review, client_name: e.target.value })} placeholder="e.g. Mr. Johnson" className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="review-client-name" />
            </div>
            <div className="rounded-md border border-white/10 bg-black/20 p-3 text-xs text-white/70">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-white/40">Agent:</span> <span className="text-white">{itin.agent?.name || "—"}</span></div>
                <div><span className="text-white/40">Trip:</span> <span className="text-white">{itin.title}</span></div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox id="allow-public" checked={review.allow_public} onCheckedChange={(v) => setReview({ ...review, allow_public: Boolean(v) })} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black" data-testid="review-allow-public" />
              <Label htmlFor="allow-public" className="text-xs text-white/70 leading-relaxed">
                I allow Best Time Travel &amp; Tours to display my review publicly.
              </Label>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={reviewSubmitting} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black w-full h-11 text-xs uppercase tracking-[0.25em]" data-testid="submit-review-btn">
                {reviewSubmitting ? <CircleNotch size={18} className="animate-spin" /> : "Submit Review"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
